import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Edit, CreditCard, Coins, ArrowRight, Link2, Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentRelationships = ({ customerId, customerName }) => {
  const [relationships, setRelationships] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [customerSearch, setCustomerSearch] = useState(''); // NEW: Search state
  
  const [formData, setFormData] = useState({
    role: 'payer', // 'payer' = this customer pays for someone, 'beneficiary' = someone pays for this customer
    other_customer_id: '',
    share_type: 'invoices_only',
    notes: ''
  });

  useEffect(() => {
    if (customerId) {
      fetchRelationships();
      fetchAllCustomers();
    }
  }, [customerId]);

  const fetchRelationships = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payment-relationships/customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRelationships(response.data || []);
    } catch (error) {
      console.error('Error fetching relationships:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers-with-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter: nur aktive Kunden (is_active === true) und nicht der aktuelle Kunde
      // customerId prop ist die customer.id aus der customers Collection
      const filtered = response.data.filter(c => 
        c.customer_id !== customerId && // Vergleich mit customer_id
        c.is_active === true
      );
      setAllCustomers(filtered);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // NEW: Filtered customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) {
      return allCustomers;
    }
    const searchLower = customerSearch.toLowerCase();
    return allCustomers.filter(c => {
      const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
      const email = (c.email || '').toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [allCustomers, customerSearch]);

  const payingFor = useMemo(() => relationships.filter(r => r.payer_customer_id === customerId), [relationships, customerId]);
  const paidBy = useMemo(() => relationships.filter(r => r.beneficiary_customer_id === customerId), [relationships, customerId]);

  const handleCreate = async () => {
    if (!formData.other_customer_id) {
      toast.error('Bitte wählen Sie einen Kunden aus');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        payer_customer_id: formData.role === 'payer' ? customerId : formData.other_customer_id,
        beneficiary_customer_id: formData.role === 'payer' ? formData.other_customer_id : customerId,
        share_type: formData.share_type,
        share_card: !!formData.share_card,  // Welle C
        notes: formData.notes || null
      };

      await axios.post(`${API}/payment-relationships`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Beziehung erstellt!');
      setDialogOpen(false);
      setFormData({ role: 'payer', other_customer_id: '', share_type: 'invoices_only', share_card: false, notes: '' });
      setCustomerSearch(''); // Reset search
      fetchRelationships();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleUpdate = async () => {
    if (!selectedRelationship) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/payment-relationships/${selectedRelationship.id}`, {
        share_type: selectedRelationship.share_type,
        notes: selectedRelationship.notes,
        is_active: selectedRelationship.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Beziehung aktualisiert!');
      setEditDialogOpen(false);
      setSelectedRelationship(null);
      fetchRelationships();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (relationshipId) => {
    if (!window.confirm('Beziehung wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/payment-relationships/${relationshipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Beziehung gelöscht!');
      fetchRelationships();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openEditDialog = (rel) => {
    setSelectedRelationship({ ...rel });
    setEditDialogOpen(true);
  };

  // Separate relationships into "paying for" and "paid by"

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-relationships-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Zahlungsbeziehungen
          </h3>
          <p className="text-sm text-gray-500">
            Verwalten Sie, wer für wen bezahlt
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <UserPlus className="h-4 w-4 mr-2" />
              Neue Beziehung
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Neue Zahlungsbeziehung</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Role Selection */}
              <div className="space-y-2">
                <Label>Art der Beziehung</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({...formData, role: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payer">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-green-600" />
                        {customerName} bezahlt für jemanden
                      </div>
                    </SelectItem>
                    <SelectItem value="beneficiary">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-blue-600" />
                        Jemand bezahlt für {customerName}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Customer Selection with Search */}
              <div className="space-y-2">
                <Label>
                  {formData.role === 'payer' ? 'Begünstigter Kunde' : 'Zahlender Kunde'}
                </Label>
                
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Kunde suchen..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="pl-9"
                    data-testid="relationship-customer-search"
                  />
                </div>
                
                {/* Customer Select */}
                <Select 
                  value={formData.other_customer_id} 
                  onValueChange={(v) => setFormData({...formData, other_customer_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kunde auswählen..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {filteredCustomers.length === 0 ? (
                      <div className="px-2 py-4 text-sm text-gray-500 text-center">
                        {customerSearch ? 'Keine Kunden gefunden' : 'Keine aktiven Kunden verfügbar'}
                      </div>
                    ) : (
                      filteredCustomers.map(c => (
                        <SelectItem key={c.customer_id} value={c.customer_id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{c.first_name} {c.last_name}</span>
                            <span className="text-xs text-gray-500">{c.email}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Share Type */}
              <div className="space-y-2">
                <Label>Was wird geteilt?</Label>
                <Select 
                  value={formData.share_type} 
                  onValueChange={(v) => setFormData({...formData, share_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoices_only">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Nur Rechnungen (Termine separat, eine Rechnung)
                      </div>
                    </SelectItem>
                    <SelectItem value="invoices_and_points">
                      <div className="flex items-center gap-2">
                        <Coins className="h-4 w-4" />
                        Rechnungen + Punkte/Abo teilen
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {formData.share_type === 'invoices_only' 
                    ? 'Termine werden separat geführt, aber alle auf eine Rechnung gebucht.'
                    : 'Punkte und Abos werden gemeinsam genutzt. Beide können vom selben Guthaben profitieren.'
                  }
                </p>
              </div>

              {/* Welle C: Kreditkarten-Sharing */}
              <div className="space-y-2 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!formData.share_card}
                    onChange={(e) => setFormData({...formData, share_card: e.target.checked})}
                    className="mt-0.5 h-4 w-4 rounded text-amber-600"
                    data-testid="relationship-share-card-toggle"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">💳 Kreditkarte teilen</div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Die hinterlegte Kreditkarte des Zahlers wird auch für Direktbelastungen des Begünstigten verwendet (z.B. Punkte-Kauf, Abo-Verlängerung). Begünstigter muss keine eigene Karte hinterlegen.
                    </p>
                  </div>
                </label>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notizen (optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="z.B. Ehepaar, Familie..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); setCustomerSearch(''); }}>
                Abbrechen
              </Button>
              <Button onClick={handleCreate} data-testid="relationship-create-button">
                Beziehung erstellen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* No relationships message */}
      {relationships.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Keine Zahlungsbeziehungen vorhanden</p>
            <p className="text-sm text-gray-400 mt-1">
              Erstellen Sie eine Beziehung, um Rechnungen zusammenzufassen
            </p>
          </CardContent>
        </Card>
      )}

      {/* Paying For Section */}
      {payingFor.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-600" />
              {customerName} bezahlt für:
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {payingFor.map(rel => (
              <div 
                key={rel.id} 
                className={`flex items-center justify-between p-3 rounded-lg border ${rel.is_active ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                data-testid={`relationship-paying-${rel.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{rel.beneficiary_name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Badge variant={rel.share_type === 'invoices_and_points' ? 'default' : 'outline'} className="text-xs">
                        {rel.share_type === 'invoices_and_points' ? '💰 + Punkte' : '💰 Nur Rechnungen'}
                      </Badge>
                      {rel.share_card && (
                        <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:text-amber-300">
                          💳 Karte
                        </Badge>
                      )}
                      {rel.notes && <span>• {rel.notes}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!rel.is_active && (
                    <Badge variant="secondary">Inaktiv</Badge>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(rel)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(rel.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Paid By Section */}
      {paidBy.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              {customerName} wird bezahlt von:
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {paidBy.map(rel => (
              <div 
                key={rel.id} 
                className={`flex items-center justify-between p-3 rounded-lg border ${rel.is_active ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}
                data-testid={`relationship-paidby-${rel.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{rel.payer_name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Badge variant={rel.share_type === 'invoices_and_points' ? 'default' : 'outline'} className="text-xs">
                        {rel.share_type === 'invoices_and_points' ? '💰 + Punkte' : '💰 Nur Rechnungen'}
                      </Badge>
                      {rel.share_card && (
                        <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:text-amber-300">
                          💳 Karte
                        </Badge>
                      )}
                      {rel.notes && <span>• {rel.notes}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!rel.is_active && (
                    <Badge variant="secondary">Inaktiv</Badge>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(rel)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(rel.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <p className="text-sm text-blue-900">
            <strong>ℹ️ Hinweis zur Rechnungsstellung:</strong><br/>
            Wenn eine Beziehung aktiv ist, werden die Termine des Begünstigten automatisch 
            auf die Rechnung des Zahlers gebucht. Die Rechnung zeigt klar an, welche Termine 
            für welche Person sind.
          </p>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Beziehung bearbeiten</DialogTitle>
          </DialogHeader>
          {selectedRelationship && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>{selectedRelationship.payer_name}</strong>
                  <ArrowRight className="inline h-4 w-4 mx-2" />
                  bezahlt für
                  <ArrowRight className="inline h-4 w-4 mx-2" />
                  <strong>{selectedRelationship.beneficiary_name}</strong>
                </p>
              </div>

              <div className="space-y-2">
                <Label>Was wird geteilt?</Label>
                <Select 
                  value={selectedRelationship.share_type} 
                  onValueChange={(v) => setSelectedRelationship({...selectedRelationship, share_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoices_only">Nur Rechnungen</SelectItem>
                    <SelectItem value="invoices_and_points">Rechnungen + Punkte/Abo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notizen</Label>
                <Input
                  value={selectedRelationship.notes || ''}
                  onChange={(e) => setSelectedRelationship({...selectedRelationship, notes: e.target.value})}
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label>Beziehung aktiv</Label>
                  <p className="text-xs text-gray-500">Deaktivierte Beziehungen werden bei der Rechnungsstellung ignoriert</p>
                </div>
                <Switch
                  checked={selectedRelationship.is_active}
                  onCheckedChange={(v) => setSelectedRelationship({...selectedRelationship, is_active: v})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdate}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default React.memo(PaymentRelationships);
